import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus, faSignOut, faUnlink, faPencilAlt,
} from '@fortawesome/pro-light-svg-icons'

import { Box, Button, FormField } from '@domain-group/suit-theme'
import { digObject } from '@domain-group/javascript-utils'

import { TaskListItemModel, TemplatePageModel } from '@domain-group/realhub-backend-api-types'

import useLoginToRealhub from '@hooks/useLoginToRealhub'
import useRelatedEntity from '@hooks/useRelatedEntity'
import useTemplatePage from '@hooks/useTemplatePage'

import handleCallbackAction, { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { findRelatedEntity } from '@functions/relatedEntity'

import type { EntitiesState } from '@redux/entities'

import PagePreview from '@components/PagePreview'

import TestTemplateButton from './TestTemplateButton'

// @ts-expect-error TS7006
const confirmDelete = (deleteRelatedEntity, deleteFn) => {
  const deleteRelatedEntityPayload = {
    callbacks: {
      action: deleteFn,
    },
    toastText: 'Template Unlinked',
  }

  swal
    .fire({
      title: 'Unlink Template?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, unlink it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        deleteRelatedEntity(deleteRelatedEntityPayload)
      }
    })
}

interface TemplateProps {
  callbacks: {
    deleteRelatedEntity: () => void,
    setupTemplateTestingEntities: () => void,
  },
  entities: EntitiesState,
  taskListItem: TaskListItemModel,
  templatePage: TemplatePageModel,
}

const Template = (props: TemplateProps) => {
  const {
    callbacks: { deleteRelatedEntity },
    entities,
    taskListItem,
    templatePage,
  } = props

  const templatePagePayload = useTemplatePage(templatePage, {
    performHttpRequests: !!templatePage.id,
    requestOptions: {
      include_related_entity_owner: true,
      include_template_page_dimensions: true,
      include_template_page_permission_ids: true,
      include_template_page_preview: true,
      include_template_page_related_entities_where_subject: true,
    },
  })

  const {
    callbacks: {
      createTemplatePageFromTask,
      viewTemplatePageDetails,
    },
    creating,
    templatePage: { id, preview_image },
  } = templatePagePayload

  const hasTemplatePage = !!id

  const relatedEntityProduct = findRelatedEntity({
    entities,
    // @ts-expect-error TS2322
    item: taskListItem,
    subjectType: 'ServiceProduct',
  })
  const hasRelatedEntityProduct = !!relatedEntityProduct

  const relatedEntityTemplatePage = findRelatedEntity({
    entities,
    filters: {
      'data.template_type': 'new',
    },
    // @ts-expect-error TS2322
    item: taskListItem,
    subjectType: 'TemplatePage',
  })

  const relatedEntityPayload = useRelatedEntity({
    data: {
      template_type: 'new',
    },
    owner_id: taskListItem.id,
    owner_type: 'TaskListItem',
    subject_type: 'TemplatePage',
    ...relatedEntityTemplatePage,
  })

  const {
    callbacks: { createOrEditRelatedEntity, deleteRelatedEntity: deleteFn },
  } = relatedEntityPayload

  const loginToRealhubPayload = useLoginToRealhub({
    // @ts-expect-error
    redirect: { key: 'editTemplatePage', params: { templatePageId: id } },
  })

  const { callbacks: { loginToRealhub } } = loginToRealhubPayload

  const createTemplatePageFromTaskPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: createTemplatePageFromTask,
      // @ts-expect-error TS2322
      buildActionParams: () => [
        taskListItem,
        { title: taskListItem.title },
        { include_template_page_related_entities_where_subject: true },
      ],
    },
  }

  return (
    <FormField boxProps={{ marginRight: 'large', width: 345 }} label="Template">
      <Box
        alignItems="center"
        flexDirection="column"
        height={225}
        justifyContent="center"
        padding="large"
        position="relative"
        variant="whiteGrey"
      >
        {!hasTemplatePage && (
          <Box flexDirection="column" width="auto">
            {hasRelatedEntityProduct && (
              <Button
                buttonStyle="primaryEdit"
                icon={<FontAwesomeIcon icon={faPlus} />}
                loading={creating}
                onClick={() => handleCallbackAction(createTemplatePageFromTaskPayload)}
                marginBottom="medium"
                size="medium"
                width="auto"
              >
                Create Template
              </Button>
            )}

            <Button
              buttonStyle={hasRelatedEntityProduct ? 'secondaryEdit' : 'primaryEdit'}
              icon={<FontAwesomeIcon icon={faPlus} />}
              loading={creating}
              onClick={() => createOrEditRelatedEntity()}
              size="medium"
              width="auto"
            >
              Link Template
            </Button>
          </Box>
        )}

        {!!preview_image && (
          <PagePreview
            holderHeight={190}
            holderPadding={8}
            holderWidth={240}
            imageHeight={digObject(templatePage, 'dimensions.pixels.height')}
            imageUrl={preview_image}
            imageWidth={digObject(templatePage, 'dimensions.pixels.width')}
          />
        )}
      </Box>

      <Box marginTop="medium" width="auto">
        <Button
          buttonStyle="secondaryEdit"
          disabled={!hasTemplatePage}
          icon={<FontAwesomeIcon icon={faPencilAlt} />}
          marginRight="medium"
          onClick={() => loginToRealhub()}
          size="medium"
          width="calc(50% - 4px)"
        >
          Edit
        </Button>

        <TestTemplateButton
          taskListItem={taskListItem}
          templatePage={templatePage}
        />
      </Box>

      <Box marginTop="medium" width="auto">
        <Button
          buttonStyle="secondaryUtility"
          disabled={!hasTemplatePage}
          icon={<FontAwesomeIcon icon={faSignOut} />}
          marginRight="medium"
          onClick={() => viewTemplatePageDetails()}
          size="medium"
          width="calc(50% - 4px)"
        >
          Details
        </Button>

        <Button
          buttonStyle="secondaryUtility"
          disabled={!hasTemplatePage}
          icon={<FontAwesomeIcon icon={faUnlink} />}
          onClick={() => confirmDelete(deleteRelatedEntity, deleteFn)}
          size="medium"
          width="calc(50% - 4px)"
        >
          Unlink
        </Button>
      </Box>
    </FormField>
  )
}

export default Template
